/**
 * @fileoverview Flagged Transactions — focused single-agent view
 * @module jobs/phases/review/flaggedTransactions/FocusedHeadView
 *
 * Replaces the master table when an agent is opened (per the ticket):
 * outcome + reasoning at the top, a compact scope strip (review window,
 * basis, last run), lock/unlock + re-run controls, the parked proposed
 * summary when locked, per-director exposure for insolvent trading, agent
 * history, and the transaction table.
 */

import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { Check, GitPullRequestArrow, Loader2, Pencil, X } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
  type ChangeHistoryEntry,
} from "@/components/jobs/phases/gather/summarization/shared-panels/ChangeHistory";
import { UnifiedFeed } from "@/components/jobs/phases/gather/summarization/shared-panels";
import { ManagedAgentLivePanel } from "@/components/jobs/phases/gather/summarization/ManagedAgentLivePanel";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import { ChevronDown } from "lucide-react";
import { Callout } from "./Callout";
import { AgentText, AgentProse, StatuteBadge } from "./AgentText";
import { AgentStatusBadge } from "./AgentStatusBadge";
import { DutyContextBanner } from "../DutyContextBanner";
import { displayProvision } from "../duties";
import {
  affirmativeDefenceIndicators,
  agentText,
  fmtDate,
  fmtDateTime,
  fmtMoney,
  INSOLVENCY_BASIS_LABEL,
  HEAD_ELEMENTS,
  OUTCOME_LABEL,
  outcomeDisplay,
  type WorkspaceHeadUI,
  type WorkspaceUI,
} from "./flaggedShared";

function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="inline-flex items-baseline gap-1.5 rounded-md border bg-muted/40 px-2.5 py-1">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <span className="text-xs font-medium tabular-nums">{value}</span>
    </div>
  );
}
import { TransactionTable } from "./TransactionTable";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";

export function FocusedHeadView(props: {
  jobId: Id<"jobs">;
  head: WorkspaceHeadUI;
  isViewer: boolean;
  insolvency: WorkspaceUI["insolvency"];
  onEditInsolvencyDate: () => void;
  onNavigateToHead?: (headKey: WorkspaceHeadUI["headKey"]) => void;
}) {
  const {
    jobId,
    head,
    isViewer,
    insolvency,
    onEditInsolvencyDate,
    onNavigateToHead,
  } = props;

  const resolveProposedSummary = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .resolveProposedSummary,
    ),
  });
  const editHeadOutput = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .editHeadOutput,
    ),
  });

  // Inline editing of the agent's status / conclusion / reasoning.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    outcome: head.outcome ?? "flags_identified",
    headline: head.headline ?? "",
    reasoning: head.reasoning ?? "",
  });

  const startEditing = () => {
    setDraft({
      outcome: head.outcome ?? "flags_identified",
      headline: head.headline ?? "",
      reasoning: head.reasoning ?? "",
    });
    setEditing(true);
  };

  const saveEdits = () => {
    editHeadOutput.mutate(
      {
        jobId,
        headKey: head.headKey,
        updates: {
          outcome: draft.outcome,
          headline: draft.headline.trim() || undefined,
          reasoning: draft.reasoning,
        },
      },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success(
            head.locked
              ? "Agent output updated"
              : "Agent output updated — lock the output to stop the next run overwriting your edit",
          );
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
      },
    );
  };

  // Full task row (activity log + live stream) powers the unified agent feed.
  // `enabled: false` still opens a Convex subscription, so idle until we
  // have a real id rather than sending "".
  const { data: taskDetail } = useQuery(
    convexQueryOrIdle(
      api.jobs.summarization.publicQueries.getSummarizationTaskDetail,
      head.taskId ? { taskId: head.taskId } : undefined,
    ),
  );

  // Resolve practitioner identities for the standard audit trail.
  const historyUserIds = useMemo<Array<Id<"users">>>(() => {
    const ids = new Set<Id<"users">>();
    for (const change of head.changeHistory) {
      const userId = (change as { userId?: Id<"users"> }).userId;
      if (userId) ids.add(userId);
    }
    return Array.from(ids);
  }, [head.changeHistory]);
  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, { ids: historyUserIds }),
    enabled: historyUserIds.length > 0,
  });
  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) map.set(u._id, u);
    return map;
  }, [historyUsers]);

  // The entitlement register measures — and caps — this head's recoverable, so
  // its absence is a finding in itself rather than a quiet zero.
  const measuredByRegister = head.headKey === "defeat_employee_entitlements";
  const { data: register } = useQuery({
    ...convexQuery(
      api.jobs.summarization.agents.flaggedTransactions.publicQueries
        .getEmployeeRegisterCoverage,
      { jobId },
    ),
    enabled: measuredByRegister,
  });
  const registerGap =
    measuredByRegister && register && register.coverage !== "figures"
      ? register
      : null;
  // Amounts for some employees but not all: the register total understates the
  // entitlements, so it is a floor for the recoverable rather than a ceiling.
  const registerPartial =
    measuredByRegister &&
    register &&
    register.coverage === "figures" &&
    register.quantifiedCount < register.employeeCount
      ? register
      : null;

  const elements = HEAD_ELEMENTS[head.headKey];
  // Heads whose liability needs no proof of insolvency: the case-wide date is
  // context only, so the chip says so rather than implying a gating date.
  const noInsolvencyElement = elements?.noInsolvencyElement;
  // Heads with a purpose / fault element the agent does not assess: name the
  // element and whose call it is, rather than presenting a bare total.
  const subjective = elements?.subjective;

  return (
    <div className="flex flex-col gap-4">
      {/* Scope, statutory window, per-director exposure, then outcome */}
      <div className="group/output rounded-xl border border-border bg-background p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        {/* Title */}
        <div className="mb-3 flex flex-wrap items-center gap-2 border-b pb-3">
          <h2 className="text-base font-semibold">{head.name}</h2>
          <AgentStatusBadge
            status={head.status}
            basisNote={head.reviewWindow.basisNote}
          />
          <StatuteBadge>
            {displayProvision("recoverable", head.headKey, head.provision)}
          </StatuteBadge>
        </div>

        <DutyContextBanner
          jobId={jobId}
          workspace="recoverable"
          headKey={head.headKey}
          className="mb-3"
        />

        {/* Scope stats */}
        <div className="flex flex-wrap gap-2">
          <StatChip
            label="Window"
            value={`${head.reviewWindow.start ? fmtDate(head.reviewWindow.start) : "open"} → ${head.reviewWindow.end ? fmtDate(head.reviewWindow.end) : "—"}`}
          />
          {head.windowLabels.start !== "Window start" && (
            <StatChip
              label="Anchors"
              value={`${head.windowLabels.start} → ${head.windowLabels.end}`}
            />
          )}
          <button
            type="button"
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 hover:bg-muted/60 disabled:cursor-default disabled:hover:bg-muted/40 ${
              noInsolvencyElement
                ? "border-dashed bg-transparent"
                : "bg-muted/40"
            }`}
            onClick={() => !isViewer && onEditInsolvencyDate()}
            disabled={isViewer}
            title={noInsolvencyElement ?? agentText(insolvency.note)}
          >
            {noInsolvencyElement ? (
              <>
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Insolvency
                </span>
                <span className="text-xs font-medium">Not an element</span>
                <span className="text-[10px] text-muted-foreground">
                  (case-wide:{" "}
                  {insolvency.date ? fmtDate(insolvency.date) : "unavailable"})
                </span>
              </>
            ) : (
              <>
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Est Insolvency Date
                </span>
                <span className="text-xs font-medium tabular-nums">
                  {insolvency.date ? fmtDate(insolvency.date) : "unavailable"}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  (
                  {INSOLVENCY_BASIS_LABEL[insolvency.basis] ??
                    insolvency.basis.replace(/_/g, " ")}
                  )
                </span>
              </>
            )}
            {!isViewer && <Pencil className="h-3 w-3 text-muted-foreground" />}
          </button>
          {subjective && (
            <div
              className="inline-flex items-center gap-1.5 rounded-md border border-dashed px-2.5 py-1"
              title={subjective.note}
            >
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                {subjective.label}
              </span>
              <span className="text-xs font-medium">
                Practitioner to assess
              </span>
              <span className="text-[10px] text-muted-foreground">
                ({subjective.provision})
              </span>
            </div>
          )}
          {head.decisions.recoverableValue > 0 && (
            <StatChip
              label="Est Recoverable"
              value={fmtMoney(head.decisions.recoverableValue)}
            />
          )}
          <StatChip label="Last run" value={fmtDateTime(head.lastRunAt)} />
        </div>
        <Callout tone="legal" title="Statutory window" className="mt-3">
          <AgentText text={head.reviewWindow.basisNote} />
        </Callout>
        {registerGap && (
          <Callout
            tone="critical"
            title="No employee records for the review window"
            className="mt-3"
          >
            <p>
              {registerGap.coverage === "none"
                ? "No employee entitlement records have been identified for this job, so no entitlements can be traced to the period under review."
                : `${registerGap.employeeCount} employee${registerGap.employeeCount === 1 ? "" : "s"} are on the register, but none carry entitlement amounts.`}{" "}
              The recoverable under s596AC is the shortfall in entitlements a
              transaction caused, so it cannot be quantified from the current
              material: transactions raised here carry no dollar figure, and
              this head's recoverable stays at nil until the records are
              obtained.
            </p>
            <p className="mt-1.5">
              To quantify: the payroll ledger, the ROCAP employee schedule, STP
              or PAYG summaries, super fund statements, and any FEG data. Their
              absence is also relevant to the books-and-records offences
              (s286/s344) and to the s588E presumption.
            </p>
          </Callout>
        )}
        {registerPartial && (
          <Callout
            tone="warning"
            title="Employee register is incomplete"
            className="mt-3"
          >
            <p>
              {registerPartial.quantifiedCount} of{" "}
              {registerPartial.employeeCount} employees carry entitlement
              amounts, so the register total of{" "}
              {fmtMoney(registerPartial.totalEntitlements)} understates what is
              owed. Treat it as a floor for the recoverable under s596AC, not a
              ceiling — a shortfall may exceed it once the remaining{" "}
              {registerPartial.employeeCount - registerPartial.quantifiedCount}{" "}
              employee
              {registerPartial.employeeCount -
                registerPartial.quantifiedCount ===
              1
                ? " is"
                : "s are"}{" "}
              quantified.
            </p>
          </Callout>
        )}
        {subjective && (
          <Callout
            tone="legal"
            title={`${subjective.label} — For Practitioner Assessment`}
            className="mt-3"
          >
            <p>{subjective.note}</p>
            {head.subjectiveElement &&
              head.subjectiveElement.undocumentedCount > 0 && (
                <p className="mt-1.5 font-medium">
                  {head.subjectiveElement.undocumentedCount} transaction
                  {head.subjectiveElement.undocumentedCount === 1
                    ? ""
                    : "s"}{" "}
                  totalling {fmtMoney(head.subjectiveElement.undocumentedValue)}{" "}
                  have no documented material on{" "}
                  {subjective.label.toLowerCase()}.
                </p>
              )}
          </Callout>
        )}

        {/* s588FE(5) is an extended limitation period over s588FA / s588FB,
            not a claim of its own. Flags raised before the character became a
            gate are surfaced here for re-assessment (MOR-1264). */}
        {head.extendedReach && head.extendedReach.uncharacterisedCount > 0 && (
          <Callout
            tone="warning"
            title="Transactions with no s588FA / s588FB character"
            className="mt-3"
          >
            <p>
              {head.extendedReach.uncharacterisedCount} transaction
              {head.extendedReach.uncharacterisedCount === 1 ? "" : "s"}{" "}
              totalling {fmtMoney(head.extendedReach.uncharacterisedValue)}{" "}
              {head.extendedReach.uncharacterisedCount === 1 ? "was" : "were"}{" "}
              raised under s588FE(5) with no recorded s588FA / s588FB character.
              s588FE(5) only extends the reach of an unfair preference (s588FA)
              or an uncommercial transaction (s588FB), so each transaction needs
              a recorded test and a basis explaining how it applies. Re-run this
              head to re-assess them, or reject the ones that cannot be
              characterised.
            </p>
          </Callout>
        )}

        {/* Per-director exposure: the insolvent-trading head only. Any other
            head that carries a stale perDirector payload never renders the
            s588M panel (QA R-07). */}
        {head.headKey === "insolvent_trading" &&
          head.perDirector &&
          head.perDirector.length > 0 && (
            <div className="mt-3 border-t pt-3">
              <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Per-Director Est Exposure
              </h4>
              <div className="flex flex-col gap-2">
                {head.perDirector.map((d, i) => {
                  const defenceIndicators = affirmativeDefenceIndicators(
                    Array.isArray(d.defenceIndicators)
                      ? d.defenceIndicators
                      : null,
                  );
                  return (
                    <div key={i} className="rounded-lg border bg-muted/30 p-3">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <p className="text-sm font-medium">
                          {String(d.directorName)}
                        </p>
                        {typeof d.estimatedLoss === "number" && (
                          <p className="font-mono text-sm font-semibold tabular-nums">
                            {fmtMoney(d.estimatedLoss)}{" "}
                            <span className="text-[10px] font-normal text-muted-foreground">
                              est. s588M loss
                            </span>
                          </p>
                        )}
                      </div>
                      {defenceIndicators.length > 0 && (
                        <Collapsible className="mt-1">
                          <CollapsibleTrigger className="group flex w-full items-center gap-1 text-left text-[11px] font-medium text-amber-700 dark:text-amber-400">
                            <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
                            Possible defence evidence present (
                            {defenceIndicators.length})
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <ul className="mt-0.5 list-disc space-y-0.5 pl-6 text-[11px] text-amber-700 dark:text-amber-400">
                              {defenceIndicators.map((indicator, idx) => (
                                <li key={idx}>{indicator}</li>
                              ))}
                            </ul>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        {/* Recoverable transactions */}
        <div className="mt-3 border-t pt-3">
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Recoverable Transactions
          </h4>
          <TransactionTable
            jobId={jobId}
            headKey={head.headKey}
            isViewer={isViewer}
            onNavigateToHead={onNavigateToHead}
          />
        </div>

        {/* Outcome + reasoning (practitioner-editable) */}
        <div className="mt-3 border-t pt-3">
          <Collapsible defaultOpen>
            <CollapsibleTrigger className="group flex w-full items-center justify-between">
              <div className="flex items-center gap-2">
                <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                  Details
                </h4>
                {(() => {
                  const display = outcomeDisplay(head);
                  return (
                    <Badge color={display.color} variant="soft" size="sm">
                      {display.label}
                    </Badge>
                  );
                })()}
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              {editing ? (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Select
                      value={draft.outcome}
                      onValueChange={(v) =>
                        setDraft((d) => ({
                          ...d,
                          outcome: v as typeof d.outcome,
                        }))
                      }
                    >
                      <SelectTrigger className="h-8 w-56 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(OUTCOME_LABEL).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="ml-auto flex items-center gap-1.5">
                      <Button
                        size="sm"
                        className="h-8 gap-1.5"
                        disabled={editHeadOutput.isPending}
                        onClick={saveEdits}
                      >
                        {editHeadOutput.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Check className="h-3.5 w-3.5" />
                        )}
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 gap-1.5"
                        onClick={() => setEditing(false)}
                      >
                        <X className="h-3.5 w-3.5" /> Cancel
                      </Button>
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                      Conclusion
                    </p>
                    <Input
                      value={draft.headline}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, headline: e.target.value }))
                      }
                      className="text-sm font-medium"
                    />
                  </div>
                  <div>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                      Reasoning
                    </p>
                    <Textarea
                      value={draft.reasoning}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, reasoning: e.target.value }))
                      }
                      rows={8}
                      className="resize-y text-sm leading-relaxed"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-2">
                    <p className="mt-2 flex-1 text-sm font-medium leading-relaxed">
                      {head.headline ? (
                        <AgentText text={head.headline} />
                      ) : (
                        "Not yet assessed."
                      )}
                    </p>
                    {!isViewer && (
                      <button
                        type="button"
                        className="mt-1 shrink-0 rounded p-1 text-muted-foreground/0 transition-colors hover:bg-muted hover:text-foreground group-hover/output:text-muted-foreground"
                        title="Edit the agent's status, conclusion, and reasoning"
                        onClick={startEditing}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  {head.reasoning && (
                    <AgentProse
                      text={head.reasoning}
                      className="mt-2 text-sm leading-relaxed text-muted-foreground"
                    />
                  )}
                </>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Proposed summary (parked by a lock) */}
        {head.proposedSummary && (
          <div className="mt-3 rounded-lg border border-violet-300 bg-violet-50/60 p-3 dark:border-violet-800 dark:bg-violet-950/20">
            <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-violet-700 dark:text-violet-300">
              <GitPullRequestArrow className="h-3.5 w-3.5" />
              Proposed output update (
              {fmtDateTime(head.proposedSummary.proposedAt)})
            </div>
            <p className="text-xs">
              <AgentText
                text={String(head.proposedSummary.after.headline ?? "")}
              />
            </p>
            {!isViewer && (
              <div className="mt-2 flex gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 text-xs"
                  onClick={() =>
                    void resolveProposedSummary
                      .mutateAsync({
                        jobId,
                        headKey: head.headKey,
                        accept: true,
                      })
                      .then(() => toast.success("Update applied"))
                      .catch((e: unknown) =>
                        toast.error(e instanceof Error ? e.message : "Failed"),
                      )
                  }
                >
                  <Check className="h-3 w-3" /> Agree
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 text-xs"
                  onClick={() =>
                    void resolveProposedSummary
                      .mutateAsync({
                        jobId,
                        headKey: head.headKey,
                        accept: false,
                      })
                      .then(() => toast.success("Update dismissed"))
                      .catch((e: unknown) =>
                        toast.error(e instanceof Error ? e.message : "Failed"),
                      )
                  }
                >
                  <X className="h-3 w-3" /> Disagree
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Unified agent feed — same live timeline as the data agents */}
        {taskDetail && (
          <div className="mt-3 border-t pt-3">
            <Collapsible defaultOpen={head.status === "updating"}>
              <CollapsibleTrigger className="group flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                    Agent Feed
                  </h4>
                  {head.status === "updating" && (
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  )}
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                {/* liveText is currently never populated by the runner
                    (platform-wide); only render the stream panel when it
                    actually has content so we don't show a dead box. */}
                {head.status === "updating" &&
                  (taskDetail.liveText ?? "").length > 0 && (
                    <ManagedAgentLivePanel
                      liveText={taskDetail.liveText ?? ""}
                      isRunning
                    />
                  )}
                <UnifiedFeed
                  task={taskDetail}
                  subAgents={[]}
                  taskTree={
                    (taskDetail.workingMemory as { taskTree?: unknown[] })
                      ?.taskTree as never
                  }
                  isRunning={head.status === "updating"}
                />
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {/* Output history — locks, proposals, run summaries */}
        {head.changeHistory.length > 0 && (
          <div className="mt-3 border-t pt-3">
            <ChangeHistory
              history={head.changeHistory as ChangeHistoryEntry[]}
              usersById={historyUsersById}
              title="History"
            />
          </div>
        )}

        <AiDisclaimer className="mt-4" />
      </div>
    </div>
  );
}
